import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebaseConfig';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, writeBatch } from 'firebase/firestore';
import {
  Button, TextField, Container, Box, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Snackbar, Alert, Typography, Card, TableContainer, Stack, Skeleton, alpha
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Admin = () => {
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsLoggedIn(true);
        fetchSessions().finally(() => setLoading(false));
      } else {
        setIsLoggedIn(false);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchSessions = async () => {
    try {
      const sessionsCollection = collection(db, 'loginSessions');
      const sessionsSnapshot = await getDocs(sessionsCollection);
      const sessionsList = sessionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Sort sessions by time in descending order
      const sortedSessions = sessionsList.sort((a, b) => new Date(b.time) - new Date(a.time));
      setSessions(sortedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setSnackbarMessage('Error fetching sessions');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleLogin = async () => {
    setLoggingIn(true);
    try {
      await signInWithEmailAndPassword(auth, adminEmail, adminPassword);
      setSnackbarMessage('Login successful');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Login failed: ' + error.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoggingIn(false);
    }
  };

  const handleClearSessions = async () => {
    setLoading(true);
    try {
      const batch = writeBatch(db);
      const sessionsCollection = collection(db, 'loginSessions');
      const sessionsSnapshot = await getDocs(sessionsCollection);
      sessionsSnapshot.docs.forEach(doc => batch.delete(doc.ref));
      await batch.commit();
      fetchSessions();
      setSnackbarMessage('Sessions cleared successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error clearing sessions:', error);
      setSnackbarMessage('Error clearing sessions');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const tableColumn = ["Email", "Time", "IP", "Location"];
    const tableRows = [];

    sessions.forEach((session) => {
      const formattedTime = new Date(session.time).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
      const sessionData = [
        session.email,
        formattedTime,
        session.ip,
        session.location,
      ];
      tableRows.push(sessionData);
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 10,
      theme: 'striped',
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [26,29,36] }
    });

    doc.save('login_sessions.pdf');
  };


  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loading && !isLoggedIn) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: '#0a0a0b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#FFFFFF' }} />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: '#0a0a0b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container component="main" maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: '#1a1a1b',
              padding: { xs: 3, sm: 4 },
              borderRadius: '16px',
              border: '1px solid #3F3F46',
              animation: 'fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              '@keyframes fadeIn': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(20px) scale(0.98)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0) scale(1)',
                },
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: '-1px',
                borderRadius: '16px',
                padding: '1px',
                background: 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                pointerEvents: 'none',
                opacity: 0.5,
              },
            }}
          >
            <Box
              sx={{
                m: 1,
                mb: 2,
                bgcolor: '#3F3F46',
                width: 48,
                height: 48,
                border: '1px solid #52525B',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'scale(1.05)',
                  bgcolor: '#52525B',
                },
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
            </Box>
            <Typography
              component="h1"
              variant="h4"
              sx={{
                mb: 1,
                color: '#FFFFFF',
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              Admin Login
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mb: 4,
                color: '#A1A1AA',
                textAlign: 'center',
              }}
            >
              Sign in to access the admin dashboard
            </Typography>
            <Box sx={{ width: '100%' }}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1a1a1b',
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: '#262626',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#262626',
                      '& fieldset': {
                        borderColor: '#FFFFFF',
                        borderWidth: '1px',
                      },
                    },
                  },
                }}
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1a1a1b',
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: '#262626',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#262626',
                      '& fieldset': {
                        borderColor: '#FFFFFF',
                        borderWidth: '1px',
                      },
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                fullWidth
                onClick={handleLogin}
                disabled={loggingIn}
                sx={{
                  height: '48px',
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  backgroundColor: '#FFFFFF',
                  color: '#0a0a0b',
                  boxShadow: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: '#F4F4F5',
                    boxShadow: 'none',
                  },
                }}
              >
                {loggingIn ? <CircularProgress size={24} sx={{ color: '#0a0a0b' }} /> : 'Sign In'}
              </Button>
            </Box>
          </Box>
        </Container>

        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: '100%', borderRadius: 2 }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            mb: 4,
            gap: 2
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 1
              }}
            >
              Admin Dashboard
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: 600
              }}
            >
              View login session history and manage system data.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} width={{ xs: '100%', sm: 'auto' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToPDF}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                py: 1.5,
                borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                }
              }}
            >
              Export Sessions
            </Button>
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={handleClearSessions}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                py: 1.5,
                borderColor: theme => alpha(theme.palette.error.main, 0.3),
                color: 'error.main',
                '&:hover': {
                  borderColor: 'error.main',
                  bgcolor: theme => alpha(theme.palette.error.main, 0.04),
                }
              }}
            >
              Clear Sessions
            </Button>
          </Stack>
        </Box>

        <Card elevation={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell>
                        <Skeleton
                          animation="wave"
                          height={24}
                          width="80%"
                          sx={{
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                            borderRadius: 1
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Skeleton
                          animation="wave"
                          height={24}
                          width="70%"
                          sx={{
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                            borderRadius: 1
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Skeleton
                          animation="wave"
                          height={24}
                          width={120}
                          sx={{
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                            borderRadius: 1
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Skeleton
                          animation="wave"
                          height={24}
                          width="60%"
                          sx={{
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                            borderRadius: 1
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : sessions.length > 0 ? (
                  sessions.map(session => (
                    <TableRow
                      key={session.id}
                      sx={{
                        '&:hover': {
                          bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    >
                      <TableCell>{session.email}</TableCell>
                      <TableCell>{new Date(session.time).toLocaleString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true,
                      })}</TableCell>
                      <TableCell>{session.ip}</TableCell>
                      <TableCell>{session.location}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <AdminPanelSettingsIcon sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />
                        <Typography variant="h6">No sessions available</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Login sessions will appear here
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Admin;
